import { useRef, useState, type ReactNode } from 'react';
import { DirectUploadProvider } from '@docflow/react-activestorage-provider';
import { Loader2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { tokenStorage } from '../../utils/api-client';
import { directUploadOrigin } from '../../utils/direct-upload-origin';

interface UploadFileButtonProps {
  /** What the file picker offers, as an `<input accept>`. */
  accept: string;
  /** Largest file let through to the upload, checked before a byte is sent. */
  maxBytes: number;
  children: ReactNode;
  /** The file is in storage: here is the signed id to create the row with. */
  onUploaded: (signedId: string, file: File) => void;
  /** Refused before the upload (too large), or the upload failed. */
  onError: (reason: 'too_large' | 'failed') => void;
  disabled?: boolean;
  testId?: string;
}

/**
 * A button that picks one file and uploads it straight to storage, as the
 * crag's GPX track is, handing back the signed id the API creates the row
 * from. The server checks the type and the size again; the size is checked
 * here too so nobody waits on a 40 MB upload only to be told no.
 */
export default function UploadFileButton({
  accept,
  maxBytes,
  children,
  onUploaded,
  onError,
  disabled,
  testId,
}: UploadFileButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  // A ref, not state: the provider may hold on to the `onSuccess` it was given
  // when the upload began, before a state update would have reached it.
  const fileRef = useRef<File | null>(null);

  return (
    <DirectUploadProvider
      origin={directUploadOrigin()}
      headers={{ Authorization: `Bearer ${tokenStorage.getToken() || ''}` }}
      onSuccess={(signedIds: string[]) => {
        setUploading(false);
        const file = fileRef.current;
        if (!signedIds || signedIds.length === 0 || !file) {
          onError('failed');
          return;
        }
        onUploaded(signedIds[0], file);
      }}
      render={({ handleUpload }: { handleUpload: (files: File[]) => void }) => (
        <>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            className="sr-only"
            data-testid={testId}
            onChange={(e) => {
              const picked = e.target.files?.[0];
              e.target.value = '';
              if (!picked) return;
              if (picked.size > maxBytes) {
                onError('too_large');
                return;
              }
              fileRef.current = picked;
              setUploading(true);
              handleUpload([picked]);
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || uploading}
            onClick={() => inputRef.current?.click()}
            className="gap-2"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {children}
          </Button>
        </>
      )}
    />
  );
}
